import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Logo } from '@/components/layout/Logo';
import { Button } from '@/components/ui/Button';
import { authApi } from '@/api/endpoints';
import { useToastStore } from '@/store/toast';
import { getApiErrorMessage } from '@/api/client';
import { useSeo } from '@/hooks/useSeo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPassword() {
  // Shaxsiy/JS-only sahifa: indekslanmaydi; oldingi sahifaning JSON-LD i tozalanadi
  useSeo({ title: "Parolni tiklash — SQLMaster.uz", noindex: true });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setError(t('auth.errorEmail'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      push(t('auth.codeSentGeneric'), 'success');
      navigate('/reset-password', { state: { email: email.trim().toLowerCase() } });
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 flex justify-center">
        <Logo />
      </div>

      <div className="card p-6 sm:p-8">
        <h1 className="mb-1 text-2xl font-bold">{t('auth.forgotTitle')}</h1>
        <p className="mb-6 text-sm text-content-secondary">{t('auth.forgotSubtitle')}</p>

        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('auth.email')}</label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="input pl-10"
                autoComplete="email"
                autoFocus
              />
            </div>
            {error && <p className="mt-1 text-xs text-[var(--error)]">{error}</p>}
          </div>

          <Button type="submit" loading={loading} className="w-full">
            {t('auth.forgotButton')}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-content-secondary">
          <Link to="/login" className="font-medium text-brand-600 hover:underline">
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
